// Instant player search on ?live=1 (C2; Ben's proposal #326): the nightly file of every player
// (C1, `/stats-file?file=players`, ~3.7 MB gzipped) is downloaded once in the background after
// sign-in, and the search box searches it in the page — milliseconds instead of `/player-search`'s
// 3–20 s, and every match instead of the top 15.
//
// The server search stays as the fallback (live/player-search.ts): while the file is loading, if
// it failed, or when it finds nothing (a player new since last night). So search never gets
// worse than before. A failed download is logged loudly and never breaks the page.
//
// The file also tells us, for every player, the name she's known by and her gender (her latest
// match's competition): both are noted up front, so a drop knows a picked player's gender at once.
// Plain code, no Amplify: the always-loaded picker imports it.

import type { CompetitionsResponse, PlayerIndexFile } from "@catbento/shared";
import type { LiveSearchRow } from "../canvas/player-picker.js";
import { foldText, fuzzyScoreFolded } from "../data/fuzzy-search.js";
import { rememberFamiliarName } from "../data/familiar-names.js";
import { getPlayerShortName } from "../data/player-utils.js";
import type { LiveApi } from "./api.js";
import type { PickedPlayer } from "./picked-players.js";
import { rememberWomens } from "./player-gender.js";
import { leagueStrengthRank } from "./league-strength.js";
import { topLeagueRank } from "./top-leagues.js";

interface IndexedPlayer extends PickedPlayer {
  teamId: number;
  /** Ben's Top Leagues place (0 = first), NOT_TOP when not a top league: the better of the
   *  player's own league (below) and their club's league. */
  rank: number;
  /** Their own league's place in Ben's strength order (live/league-strength.ts, #347), NOT_TOP when
   *  it isn't listed: after the Top Leagues, a stronger league's players first. */
  strength: number;
  /** Minutes this season + last (the file's `minutes`, #329): among otherwise equal matches, the
   *  more a player plays, the higher ("Messi" → Lionel, Ben's proposal). 0 in a file from before. */
  minutes: number;
  /** 1 = last played before the last two calendar years (retired, or long gone from our data),
   *  0 = recent, or unknown (a file from before `lastYear`). */
  inactive: 0 | 1;
  /** foldText(name + nickname), once — 170,000+ names are scored on every key. */
  folded: string;
  /** foldText(club), once per club: "Gabriel Arsenal" finds Arsenal's Gabriel. */
  clubFolded: string;
  /** Their nationality, ISO alpha-3, for the flag before the name — null when unknown, or the
   *  file is from before it was added. */
  nationality: string | null;
  /** Their club's country (its home league's), ISO alpha-3, for the flag after the club. */
  clubCountry: string | null;
}

interface IndexedClub {
  id: number;
  name: string;
  folded: string;
  /** How many players its league has in the file — "largest league first" (Prateek, 2026-10-22). */
  leagueSize: number;
  /** Its league's Top Leagues place, as a player's `rank`. */
  rank: number;
  /** Its league's strength place, as a player's `strength`. */
  strength: number;
  /** Its home league, for the heading's flag + name (Q-018), or null when none is known. */
  league: ClubLeague | null;
  /** That league's id: where a picked team's card opens (teams and matches, 2026-10-30). */
  leagueId: number | null;
  players: IndexedPlayer[];
}

/** A club's home league as its search heading shows it: "🏴 Arsenal · Premier League". */
export interface ClubLeague {
  name: string;
  /** ISO alpha-3 (golden.country.id), for the flag. */
  countryCode: string | null;
}

export interface PlayerIndex {
  players: IndexedPlayer[];
  clubs: IndexedClub[];
}

/** fuzzyScoreFolded's bonus when the name starts with the whole query (left out, see the search). */
const START_BONUS = 10;

/** Not a top league: after every top league's players. */
const NOT_TOP = Number.MAX_SAFE_INTEGER;

/** A competition id → its Top Leagues place (live/top-leagues.ts), or null when it isn't one. */
export type LeagueRank = (competitionId: number) => number | null;

/** /competitions → each league's Top Leagues place, in Ben's order. Since the file has each
 *  player's HOME league (#329), the Champions League no longer stands in for the Premier League
 *  in a CL week, so it keeps its own place on his list. `europeanFirst` is for a file from before
 *  (no home league): there the latest match is all we know, so a top European club competition
 *  (no country: Champions League, Europa League…) counts as first, as it did then. */
export function leagueRankFrom({ competitions }: CompetitionsResponse, europeanFirst = false): LeagueRank {
  const ranks = new Map(
    competitions.map((c) => {
      const rank = topLeagueRank({ name: c.name, countryName: c.countryName, womens: c.competitionWomens });
      return [c.id, europeanFirst && rank !== null && c.countryName === null ? 0 : rank];
    }),
  );
  return (id) => ranks.get(id) ?? null;
}

/** /competitions → each league's place in Ben's strength order (#347), or null when unlisted. */
export function leagueStrengthFrom({ competitions }: CompetitionsResponse): LeagueRank {
  const ranks = new Map(competitions.map((c) => [c.id, leagueStrengthRank({ name: c.name, countryName: c.countryName, womens: c.competitionWomens })]));
  return (id) => ranks.get(id) ?? null;
}

/** Does the file carry each player's home league + minutes (#329)? The file stayed version 1 when
 *  they were added, so an older one just has shorter rows. */
export function hasHomeLeagues(file: PlayerIndexFile): boolean {
  return file.players.some((row) => row.length > 8);
}

/** A competition id → the league, when it's a DOMESTIC one (a real country, not
 *  "International": the server's rule, db/current-league.ts), else null. */
export type DomesticLeague = (competitionId: number) => ClubLeague | null;

/** A competition id → is it a national-team one (country "International": World Cup, Nations
 *  League…)? There the player's "club" in the file is their national team. */
export type NationalTeamCompetition = (competitionId: number) => boolean;

/** /competitions → the national-team competitions. */
export function nationalTeamCompetitionFrom({ competitions }: CompetitionsResponse): NationalTeamCompetition {
  const ids = new Set(competitions.filter((c) => c.countryName === "International").map((c) => c.id));
  return (id) => ids.has(id);
}

/** /competitions → the domestic leagues, by id. */
export function domesticLeagueFrom({ competitions }: CompetitionsResponse): DomesticLeague {
  const leagues = new Map(
    competitions
      .filter((c) => c.countryName !== null && c.countryName !== "International")
      .map((c) => [c.id, { name: c.name, countryCode: c.countryCode }]),
  );
  return (id) => leagues.get(id) ?? null;
}

/** The file → what the search reads. Pure. A row naming a club the file doesn't list is a
 *  broken file → thrown, never a player without a club. `leagueRank` puts players from Ben's
 *  Top Leagues first (Ben, 2026-10-23: people mostly look for players at the bigger clubs); left
 *  out, every league counts the same. `domesticLeague` names each club's home league on its
 *  heading (Q-018); left out, headings are plain club names. */
export function buildPlayerIndex(
  file: PlayerIndexFile,
  leagueRank: LeagueRank = () => null,
  domesticLeague: DomesticLeague = () => null,
  nationalTeamCompetition: NationalTeamCompetition = () => false,
  leagueStrength: LeagueRank = () => null,
): PlayerIndex {
  const teamNames = new Map(file.teams);
  const clubFolded = new Map(file.teams.map(([id, n]) => [id, foldText(n)]));
  const playersByTeam = new Map<number, IndexedPlayer[]>();
  const leagueSize = new Map<number, number>();
  // "Recent" = last played this calendar year or last (built Oct 2026 → 2025 or 2026).
  const recentFrom = new Date(file.builtAt).getUTCFullYear() - 1;
  // A club's league = the league most of its players call home (their home league, #329; else
  // their latest match's competition), counting only its recent players when it has any (Stoke's
  // retired players' Premier League games shouldn't make Stoke a Premier League club today).
  const teamLeagues = new Map<number, Map<number, number>>();
  const recentTeamLeagues = new Map<number, Map<number, number>>();
  const count = (byTeam: Map<number, Map<number, number>>, teamId: number, competitionId: number) => {
    const leagues = byTeam.get(teamId) ?? new Map<number, number>();
    leagues.set(competitionId, (leagues.get(competitionId) ?? 0) + 1);
    byTeam.set(teamId, leagues);
  };
  const players = file.players.map(([id, name, nickname, teamId, latestCompetitionId, , lastYear, nationality, homeLeague, minutes]) => {
    const team = teamNames.get(teamId);
    if (team === undefined) throw new Error(`Player file: player ${id} names club ${teamId}, which it doesn't list`);
    const inactive = lastYear !== undefined && lastYear < recentFrom ? 1 : 0;
    // Their own league: the home league (#329). None = no domestic minutes this season or last
    // (or a file from before), so their latest match's competition is all we know.
    const competitionId = homeLeague ?? latestCompetitionId;
    // The club's count: the player's league, unless the file's club IS a national team (their
    // latest match was for their country) — then that match's competition, so "Norway" never
    // takes on the league its players happen to play in most.
    const teamLeague = nationalTeamCompetition(latestCompetitionId) ? latestCompetitionId : competitionId;
    const p: IndexedPlayer = {
      id,
      name,
      nickname,
      team,
      teamId,
      rank: leagueRank(competitionId) ?? NOT_TOP,
      strength: leagueStrength(competitionId) ?? NOT_TOP,
      minutes: minutes ?? 0,
      inactive,
      folded: foldText(`${name} ${nickname ?? ""}`),
      clubFolded: clubFolded.get(teamId)!,
      nationality: nationality ?? null,
      clubCountry: null,
    };
    const list = playersByTeam.get(teamId);
    if (list) list.push(p);
    else playersByTeam.set(teamId, [p]);
    leagueSize.set(teamLeague, (leagueSize.get(teamLeague) ?? 0) + 1);
    count(teamLeagues, teamId, teamLeague);
    if (!inactive) count(recentTeamLeagues, teamId, teamLeague);
    return p;
  });
  const clubs: IndexedClub[] = [];
  for (const [teamId, list] of playersByTeam) {
    const leagues = [...(recentTeamLeagues.get(teamId) ?? teamLeagues.get(teamId)!)].sort((a, b) => b[1] - a[1]);
    const rank = leagueRank(leagues[0]![0]) ?? NOT_TOP;
    for (const p of list) p.rank = Math.min(p.rank, rank);
    // The heading's league: the DOMESTIC league most of its current players call home, else most
    // of all its players. Else none (a national team; a player with no home league whose latest
    // match was a European cup).
    const home = (byTeam: Map<number, Map<number, number>>) =>
      [...(byTeam.get(teamId) ?? [])].filter(([c]) => domesticLeague(c) !== null).sort((a, b) => b[1] - a[1])[0]?.[0];
    const homeId = home(recentTeamLeagues) ?? home(teamLeagues);
    const league = homeId === undefined ? null : domesticLeague(homeId);
    for (const p of list) p.clubCountry = league?.countryCode ?? null;
    clubs.push({
      id: teamId,
      name: teamNames.get(teamId)!,
      folded: foldText(teamNames.get(teamId)!),
      leagueSize: leagueSize.get(leagues[0]![0])!,
      rank,
      strength: leagueStrength(leagues[0]![0]) ?? NOT_TOP,
      league,
      leagueId: league === null ? null : homeId!,
      players: list,
    });
  }
  return { players, clubs };
}

/** One local search: players whose own name matches (best first), then every player at each
 *  matching club who isn't already listed, one group per club, top leagues first, then Ben's
 *  league strength order (#347), then largest league (Ben's rule: a name always beats a club). */
export interface IndexSearch {
  players: SearchHit[];
  clubs: { club: string; league: ClubLeague | null; players: SearchHit[] }[];
  /** Clubs whose name the query starts a word of, as teams to pick (teams and matches, Ben
   *  2026-10-30: "Teams in their own group at the top", a few rows). Only those with a home
   *  league, where a team card can open. Empty unless asked for (`withTeams`). */
  teams?: TeamHit[];
}

/** A team the search found: "🏴 Arsenal · Premier League". */
export interface TeamHit {
  id: number;
  name: string;
  leagueId: number;
  league: ClubLeague;
}

/** The Teams group's length (Ben's option A: 2–4 rows). */
export const TEAM_ROWS = 4;

/** Every typed word starts a word of the club's name ("ars" → Arsenal, "man c" → Manchester
 *  City) — stricter than the players' fuzzy match, so "Rice" brings no Teams group. */
function startsWords(q: string, folded: string): boolean {
  const words = folded.split(" ");
  return q.split(" ").every((w) => words.some((cw) => cw.startsWith(w)));
}

/** A found player, with the two flags its row shows (Ben, 2026-10-23: "[nationality flag]
 *  Martin Ødegaard — Arsenal [club's country flag]"). */
export interface SearchHit extends PickedPlayer {
  nationality: string | null;
  clubCountry: string | null;
}

const collator = new Intl.Collator();
const picked = ({ id, name, nickname, team, nationality, clubCountry }: IndexedPlayer): SearchHit => ({
  id,
  name,
  nickname,
  team,
  nationality,
  clubCountry,
});

const top = (p: IndexedPlayer) => (p.rank === NOT_TOP ? 1 : 0);

/** Recent players first; then top-league players; within those, (search only) whole-word matches
 *  first; then Ben's order of the top leagues; then his league strength order (#347: the rest of
 *  the world's leagues, strongest first); then the most minutes (this season + last — Ben's
 *  "games played", #329: minutes, so a 5-minute sub doesn't count as a full match); then A–Z by
 *  the name shown, each name worked out once (a 3-letter query can match 10,000+). Not league
 *  size: tried and rejected (US college leagues are the biggest, so they came first). */
function sortByShownName(list: IndexedPlayer[], whole: ReadonlySet<IndexedPlayer> = new Set()): SearchHit[] {
  return list
    .map((p) => ({ p, shown: getPlayerShortName(p), whole: whole.has(p) ? 0 : 1 }))
    .sort((a, b) => a.p.inactive - b.p.inactive || top(a.p) - top(b.p) || a.whole - b.whole || a.p.rank - b.p.rank || a.p.strength - b.p.strength || b.p.minutes - a.p.minutes || collator.compare(a.shown, b.shown))
    .map((x) => picked(x.p));
}

/** Pure (reads only the index). `withTeams`: a module that shows teams also gets `teams`. */
export function searchPlayerIndex(index: PlayerIndex, query: string, withTeams = false): IndexSearch {
  const q = foldText(query);
  if (!q) return { players: [], clubs: [] };
  // Order (Ben, 2026-10-23: people mostly look for players at the bigger clubs):
  // 1. How each typed word matches: the start of a word of the name, inside a word, or one typo.
  //    The matcher's bonus for the name STARTING with it is left out: it put every first name
  //    "Ode…" above a surname "Ødegaard", so the league order below never came into play.
  // 2. Players who played this year or last first (Ben, 2026-10-23: Odemwingie, 10 years
  //    retired, came 2nd for "ode").
  // 3. Players from Ben's Top Leagues first.
  // 4. The typed words as WHOLE words ("Silva" → Bernardo Silva before Silvan Bröker, Q-017).
  // 5. Ben's order of the top leagues (Premier League before Serie A).
  // 6. Ben's league strength order (#347): a Superliga player before a 1st Division one.
  // 7. The most minutes this season + last (#329: Lionel first for "Messi").
  // 8. A–Z.
  const qWords = q.split(" ");
  const groups = new Map<number, IndexedPlayer[]>();
  const whole = new Set<IndexedPlayer>();
  for (const p of index.players) {
    let text = p.folded;
    let score = fuzzyScoreFolded(q, text);
    // Part of the name + the club ("Gabriel Arsenal", Ben 2026-10-23): every word matches the name
    // or the club, and at least one the name — "Arsenal" alone stays under the club headings.
    if (score === 0 && qWords.length > 1 && qWords.some((w) => fuzzyScoreFolded(w, p.folded) > 0)) {
      text = `${p.folded} ${p.clubFolded}`;
      score = fuzzyScoreFolded(q, text);
    }
    if (score === 0) continue;
    const key = text.startsWith(q) ? score - START_BONUS : score;
    const words = text.split(" ");
    if (qWords.every((w) => words.includes(w))) whole.add(p);
    const list = groups.get(key);
    if (list) list.push(p);
    else groups.set(key, [p]);
  }
  const players = [...groups].sort((a, b) => b[0] - a[0]).flatMap(([, list]) => sortByShownName(list, whole));
  const listed = new Set(players.map((p) => p.id));
  const matched = matchClubs(index, q);
  const clubs = matched
    .map(({ c }) => ({ club: c.name, league: c.league, players: sortByShownName(c.players.filter((p) => !listed.has(p.id))) }))
    .filter((g) => g.players.length > 0);
  if (!withTeams) return { players, clubs };
  return { players, clubs, teams: teamsOf(matched, q).slice(0, TEAM_ROWS) };
}

/** The clubs a folded query matches, in the club groups' order: top leagues, then league strength,
 *  then largest league. */
function matchClubs(index: PlayerIndex, q: string): { c: IndexedClub; score: number }[] {
  return index.clubs
    .map((c) => ({ c, score: fuzzyScoreFolded(q, c.folded) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => a.c.rank - b.c.rank || a.c.strength - b.c.strength || b.c.leagueSize - a.c.leagueSize || b.score - a.score || a.c.name.localeCompare(b.c.name));
}

/** The matched clubs that are teams: a home league, and every typed word starting a word of the name. */
function teamsOf(matched: readonly { c: IndexedClub }[], q: string): TeamHit[] {
  return matched
    .filter(({ c }) => c.leagueId !== null && c.league !== null && startsWords(q, c.folded))
    .map(({ c }) => ({ id: c.id, name: c.name, leagueId: c.leagueId!, league: c.league! }));
}

/** Teams only, as the search's Teams group finds them, without the players (the Match module's game
 *  search tries several splits of what's typed; each is a quick look at the clubs). Pure. */
export function searchTeams(index: PlayerIndex, query: string): TeamHit[] {
  const q = foldText(query);
  return q ? teamsOf(matchClubs(index, q), q) : [];
}

// --- the page's one copy --------------------------------------------------------------------------

type LoadState = { kind: "idle" } | { kind: "loading" } | { kind: "ready"; index: PlayerIndex } | { kind: "failed" };
let state: LoadState = { kind: "idle" };

/** The index once the file is in, else null (loading, failed, or never started). */
export function loadedPlayerIndex(): PlayerIndex | null {
  return state.kind === "ready" ? state.index : null;
}

/** Notes every player's familiar name + gender (see the top). */
function noteEveryone(file: PlayerIndexFile): void {
  for (const [id, name, nickname, , , womens] of file.players) {
    rememberFamiliarName(name, nickname);
    rememberWomens(id, womens === 1);
  }
}

/**
 * Start the download, once per page (later calls return the same promise). Never rejects: a
 * failure — including "not built yet" (the API's 404) — is logged with console.error and the
 * search keeps using the server.
 */
let started: Promise<void> | null = null;
export function startPlayerIndexLoad(api: LiveApi): Promise<void> {
  if (started) return started;
  state = { kind: "loading" };
  started = (async () => {
    try {
      // The league names (for the Top Leagues order) come with the file, so the order never
      // changes under the user's eyes; if they fail, the search uses the server, as for the file.
      const [link, comps] = await Promise.all([
        api.get<{ url: string }>("/stats-file?file=players"),
        api.get<CompetitionsResponse>("/competitions"),
      ]);
      // S3 sends it with `Content-Encoding: gzip`, so the browser unzips it (as the Stats files).
      const file = await api.download<PlayerIndexFile>(link.url, "stats-file players");
      if (file.version !== 1) throw new Error(`Player file: unknown version ${String(file.version)}`);
      const index = buildPlayerIndex(
        file,
        leagueRankFrom(comps, !hasHomeLeagues(file)),
        domesticLeagueFrom(comps),
        nationalTeamCompetitionFrom(comps),
        leagueStrengthFrom(comps),
      );
      noteEveryone(file);
      state = { kind: "ready", index };
      console.info(`[live] player file: ${index.players.length.toLocaleString()} players, built ${file.builtAt}`);
    } catch (err) {
      state = { kind: "failed" };
      console.error("[live] player file failed to load — the search uses the server instead:", err);
    }
  })();
  return started;
}

/** For tests only. */
export function forgetPlayerIndex(): void {
  state = { kind: "idle" };
  started = null;
}

/** A local search → the list's rows: name matches, then each club under its heading. `text` is
 *  the row's text (searchRowText: "name — club"). Pure. */
export function indexSearchRows(search: IndexSearch, text: (p: PickedPlayer) => string): LiveSearchRow[] {
  const row = (p: SearchHit): LiveSearchRow => ({ value: String(p.id), text: text(p), nationality: p.nationality, clubCountry: p.clubCountry });
  const teams = search.teams ?? [];
  // Teams first, under their own label, then the players under theirs (Ben's option A). No teams →
  // no labels: the list looks as it always has.
  const teamRows: LiveSearchRow[] = teams.length
    ? [
        { label: "Teams" },
        ...teams.map((t): LiveSearchRow => ({ value: teamValue(t.id), text: t.name, team: { league: t.league.name, countryCode: t.league.countryCode } })),
        ...(search.players.length || search.clubs.length ? [{ label: "Players" }] : []),
      ]
    : [];
  return [
    ...teamRows,
    ...search.players.map(row),
    ...search.clubs.flatMap((g): LiveSearchRow[] => [
      g.league ? { heading: g.club, league: g.league.name, countryCode: g.league.countryCode } : { heading: g.club },
      ...g.players.map(row),
    ]),
  ];
}

/** A team row's value in the search list ("team:12"), told apart from a player's id. */
export function teamValue(teamId: number): string {
  return `team:${teamId}`;
}

/** The team id in a row's value, or null for a player's. */
export function teamIdOfValue(value: string): number | null {
  return value.startsWith("team:") ? Number(value.slice(5)) : null;
}
